import React, { useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X } from "lucide-react";

/**
 * Reel lightbox — plays the Magee Enterprises Vimeo brand promo (video id
 * 1024765774) in a cinematic full-width overlay. Autoplays on open, closes
 * on backdrop click, × button, or Escape.
 */
const VIMEO_SRC =
  "https://player.vimeo.com/video/1024765774?badge=0&autopause=0&player_id=0&app_id=58479&autoplay=1&muted=1&title=0&byline=0&portrait=0";

export default function ReelLightbox({ open, onClose }) {
  const iframeRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => e.key === "Escape" && onClose();
    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [open, onClose]);

  // Listen for the Vimeo `ended` event and close the lightbox automatically
  // so the viewer returns to the home page after the reel finishes.
  useEffect(() => {
    if (!open) return;
    const SCRIPT_SRC = "https://player.vimeo.com/api/player.js";

    let player = null;
    let cancelled = false;

    const attach = () => {
      if (cancelled) return;
      if (!window.Vimeo || !window.Vimeo.Player) return;
      if (!iframeRef.current) return;
      try {
        player = new window.Vimeo.Player(iframeRef.current);
        player.on("ended", () => {
          onClose();
        });
      } catch (err) {
        // If the Vimeo Player fails to attach, the manual close (× / Escape)
        // still works — no user-facing regression.
      }
    };

    const existing = document.querySelector(`script[src="${SCRIPT_SRC}"]`);
    if (existing && window.Vimeo && window.Vimeo.Player) {
      attach();
    } else if (existing) {
      existing.addEventListener("load", attach, { once: true });
    } else {
      const s = document.createElement("script");
      s.src = SCRIPT_SRC;
      s.async = true;
      s.addEventListener("load", attach, { once: true });
      document.body.appendChild(s);
    }

    return () => {
      cancelled = true;
      if (player && typeof player.off === "function") {
        try {
          player.off("ended");
        } catch (e) {
          // no-op
        }
      }
    };
  }, [open, onClose]);

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.3 }}
          className="fixed inset-0 z-[100] bg-black/95 backdrop-blur-lg flex items-center justify-center px-4 py-8"
          onClick={onClose}
          data-testid="reel-lightbox"
        >
          <motion.div
            initial={{ scale: 0.94, y: 12 }}
            animate={{ scale: 1, y: 0 }}
            exit={{ scale: 0.98, y: 6 }}
            transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
            className="relative w-full max-w-5xl rounded-2xl overflow-hidden bg-black shadow-[0_40px_100px_-30px_rgba(6,182,212,0.4)] border border-white/10"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Responsive Vimeo 16:9 wrapper */}
            <div style={{ padding: "56.25% 0 0 0", position: "relative" }}>
              <iframe
                ref={iframeRef}
                src={VIMEO_SRC}
                title="Magee Enterprises — Promo 2"
                frameBorder="0"
                allow="autoplay; fullscreen; picture-in-picture; clipboard-write; encrypted-media; web-share"
                referrerPolicy="strict-origin-when-cross-origin"
                style={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  width: "100%",
                  height: "100%",
                }}
              />
            </div>

            {/* Brand chip + close button overlaid on the player */}
            <div className="absolute top-3 left-4 right-4 md:top-4 flex items-start justify-between gap-4 pointer-events-none">
              <span className="chip !bg-white/15 !text-white !border-white/25 backdrop-blur pointer-events-auto">
                <span className="live-dot" />
                Behind the shows &middot; Magee &times; Clients
              </span>
              <button
                type="button"
                onClick={onClose}
                aria-label="Close reel"
                data-testid="reel-lightbox-close"
                className="pointer-events-auto w-10 h-10 rounded-full bg-white/10 hover:bg-white/25 border border-white/25 text-white backdrop-blur flex items-center justify-center transition-colors"
              >
                <X size={16} />
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
